import type { ReactNode } from "react";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { type Theme } from "@mui/material/styles";
import { styled } from "../../../../lib/styled";

export type PopupMenuItemColor =
  "default" | "primary" | "secondary" | "warning" | "error" | "success";

export interface PopupMenuItemConfig {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  /** Theme color applied to the item's text and icon. */
  color?: PopupMenuItemColor;
}

/** A group of items rendered together, separated from adjacent sections by a divider. */
export type PopupMenuSection = PopupMenuItemConfig[];

export interface PopupMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Item groups; adjacent sections are separated by a Divider. */
  sections: PopupMenuSection[];
}

type ColoredItemColor = Exclude<PopupMenuItemColor, "default">;

const colorFromTheme: Record<ColoredItemColor, (t: Theme) => string> = {
  primary: (t) => t.palette.primary.main,
  secondary: (t) => t.palette.secondary.main,
  warning: (t) => t.palette.warning.main,
  error: (t) => t.palette.error.main,
  success: (t) => t.palette.success.main,
};

const ColoredMenuItem = styled(MenuItem)<{ $color?: ColoredItemColor }>(({ theme, $color }) =>
  $color ? { color: colorFromTheme[$color](theme) } : {}
);

const InheritedIcon = styled(ListItemIcon)({
  color: "inherit",
});

/**
 * A config-driven dropdown menu. Closes and then calls the item's onClick when an item is selected.
 * Pair with usePopupMenu to manage anchor and open state.
 */
export function PopupMenu({ anchorEl, open, onClose, sections }: PopupMenuProps) {
  function handleItemClick(onClick: () => void) {
    onClose();
    onClick();
  }

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      {sections.flatMap((section, si) => [
        ...(si > 0 ? [<Divider key={`d-${si}`} />] : []),
        ...section.map((item, ii) => (
          <ColoredMenuItem
            key={`${si}-${ii}`}
            onClick={() => handleItemClick(item.onClick)}
            $color={item.color && item.color !== "default" ? item.color : undefined}
          >
            {item.icon && <InheritedIcon>{item.icon}</InheritedIcon>}
            <ListItemText>{item.label}</ListItemText>
          </ColoredMenuItem>
        )),
      ])}
    </Menu>
  );
}
